import React from 'react';
import { useTranslation } from 'react-i18next';

const Epmvdos = () => {
  const { t } = useTranslation('help');

  return (
    <div style={styles.container}>
      {/* Title */}
      <h2 style={styles.title}>{t('epmvdos.title', 'Moderately Vulnerable DoS')}</h2>

      {/* Section (a) - Purpose */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(a)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('epmvdos.labels.purpose', 'Purpose:-')}{' '}
            </strong>
            {t(
              'epmvdos.descriptions.purpose',
              'A Denial-of-Service (DoS) test for a moderately vulnerable AI endpoint evaluates how an application handles resource exhaustion, oversized inputs, and high-velocity traffic without crashing the underlying AI agent. A Denial of Service (DoS) endpoint in the context of AI prompts refers to a vulnerability where an attacker manipulates an Artificial Intelligence (AI) or Large Language Model (LLM) API endpoint to consume excessive computing resources, making the service unavailable to legitimate users .'
            )}
          </p>
        </div>
      </div>

      {/* Section (b) - Endpoint */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(b)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('epmvdos.labels.endpoint', 'Endpoint :-')}{' '}
            </strong>
            <a
              href="http://172.31.30.217:8004/chat"
              style={styles.link}
              target="_blank"
              rel="noopener noreferrer"
            >
             http://172.31.30.217:8004/chat
            </a>
          </p>
        </div>
      </div>

      {/* Section (c) - Request Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(c)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('epmvdos.labels.requestJson', 'Request Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'epmvdos.descriptions.requestJson',
              'The simulator accepts an application/json body. AUTH_KEY(Provided By WhiteHaX) is required inside request_template and must match an authorized key. The endpoint validates it on every request and returns HTTP 401 when it is missing or invalid.'
            )}
          </p>

          {/* Request JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"headers"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Content-Type"</span>: <span style={styles.jsonString}>"application/json"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data_type"</span>: <span style={styles.jsonString}>"json"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"request_template"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"AUTH_KEY"</span>: <span style={styles.jsonString}>"&lt;authorized-key&gt;"</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"messages"</span>: [
            </div>
            <div style={styles.indent3}>
              {'{'} <span style={styles.jsonKey}>"role"</span>: <span style={styles.jsonString}>"user"</span>, <span style={styles.jsonKey}>"content"</span>: <span style={styles.jsonString}>"{"{{PROMPT}}"}"</span> {'}'}
            </div>
            <div style={styles.indent2}>],</div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"max_tokens"</span>: <span style={styles.jsonNumber}>256</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"prompt_path"</span>: <span style={styles.jsonString}>"messages.0.content"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (d) - Response Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(d)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('epmvdos.labels.responseJson', 'Response Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'epmvdos.descriptions.responseJson',
              'The message field contains the simulator output. Moderate mode may allow or block the request, and blocked identifies the selected path.'
            )}
          </p>

          {/* Response JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"endpoint"</span>: <span style={styles.jsonString}>"/chat"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"mode"</span>: <span style={styles.jsonString}>"moderate"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"error"</span>: <span style={styles.jsonBoolean}>false</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"blocked"</span>: <span style={styles.jsonBoolean}>false</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"message"</span>: <span style={styles.jsonString}>"{"{{RESP}}"}"</span>
            </div>
            <div>{'}'}</div>
          </div>

          {/* Expected Test Results */}
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t(
                'epmvdos.labels.expectedResults',
                '(e) Expected Test Results against this simulator:-'
              )}{' '}
            </strong>
            {t(
              'epmvdos.descriptions.expectedResults',
              'For a moderately vulnerable DoS simulator, the expected readiness result scores should be less than 30%, with some malicious or resource-exhaustion requests being blocked while others may be allowed.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component Styles (Dark Theme)
const styles = {
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    padding: '24px',
    color: '#e0e0e0',
    backgroundColor: '#181a1f',
    borderRadius: '8px',
    fontFamily: 'Segoe UI, Roboto, sans-serif'
  },
  title: {
    margin: '0 0 20px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '20px'
  },
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  sectionLabel: {
    width: '32px',
    flexShrink: 0,
    fontWeight: 'bold',
    color: '#a0a0a0'
  },
  sectionContent: {
    flexGrow: 1
  },
  paragraph: {
    margin: '0 0 10px 0',
    lineHeight: '1.6',
    color: '#cccccc',
    fontSize: '14px'
  },
  strongText: {
    color: '#ffffff'
  },
  link: {
    color: '#4fa3d1',
    textDecoration: 'underline'
  },
  codeBlock: {
    backgroundColor: '#0d0e11',
    color: '#e0e0e0',
    padding: '16px 20px',
    margin: '12px 0',
    borderRadius: '6px',
    border: '1px solid #2a2d34',
    fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    overflowX: 'auto',
    whiteSpace: 'pre'
  },
  indent1: {
    paddingLeft: '20px'
  },
  indent2: {
    paddingLeft: '40px'
  },
  indent3: {
    paddingLeft: '60px'
  },
  jsonKey: {
    color: '#6fc2ef'
  },
  jsonString: {
    color: '#e59373'
  },
  jsonNumber: {
    color: '#b5cea8'
  },
  jsonBoolean: {
    color: '#569cd6'
  }
};

export default Epmvdos;